import type { Credential, SocialLink } from './schema'

/**
 * Who the site is about. Every page, the sidebar, the SEO tags and the legal pages read these values,
 * so changing them here changes them everywhere.
 */

/** Google Calendar appointment page. The About credentials and the contact copy link to it. */
export const BOOKING_URL =
  'https://calendar.google.com/calendar/appointments/schedules/AcZssZ2T5FgnfyLXKmMZgSBV_QZcP2qgN759cD8hrMidAt5qQHpEwsDkmnLfKBPz-x0U9uNcuyixlg3G?gv=true'

/**
 * The live origin, no trailing slash. Set at build time by vite.config.ts (VITE_SITE_URL, else the
 * Cloudflare Pages project domain from CF_PAGES_URL, else the fallback there), the same order v1 used.
 */
declare const __SITE_URL__: string

export const site = {
  name: 'Carlo Cueto',
  fullName: 'Carlo Alberto G. Cueto',
  brand: 'GoHighLevel VA',
  /** The brand name as the sidebar logo shows it: the second part takes the accent color. */
  brandParts: ['Carlo', 'Cueto'],
  /** The logo mark, one file per theme (`light` = for light backgrounds). The 96px copies are the
   *  sidebar header (shown at 32-36px); the full ones are the Work logo faces. */
  logo: {
    light: '/images/brand/cc-mark.webp',
    dark: '/images/brand/cc-mark-light.webp',
    headerLight: '/images/brand/cc-mark-96.webp',
    headerDark: '/images/brand/cc-mark-light-96.webp',
  },
  url: __SITE_URL__,
  /** The short line under the name in the sidebar (v1 profile.role). */
  role: 'HighLevel & automation expert',
  /** The job title search engines and llms.txt read (v1 FACTS.jobTitle). */
  jobTitle: 'GoHighLevel Virtual Assistant & Automation Specialist',
  email: 'carloalbertocueto26@gmail.com',
  emailSubject: 'Project inquiry',
  replyPromise: 'Reply within one business day.',
  location: 'Philippines (GMT+8)',
  /** Two-letter country code (ISO 3166) for the Person JSON-LD address. */
  countryCode: 'PH',
  /** The clock shown live at the top of the sidebar. */
  timeZone: 'Asia/Manila',
  timePlace: 'in the Philippines',
  avatar: { src: '/images/profile/carlo.webp', alt: 'Carlo Cueto', width: 800, height: 800 },
  /** The sidebar picture (112px, 44px on the rail). */
  avatarSmall: '/images/profile/carlo-224.webp',
} as const

/** The credential shown in About. */
export const certification: Credential = {
  title: 'DISC: S/C/D, The Planner',
  detail: 'Certified Tony Robbins DISC and Motivators · ASI 2026',
  issuer: 'ASI',
  href: '/demos/plans/disc-profile.html',
  image: { src: '/images/badges/disc-badge.svg', alt: 'DISC profile badge: S/C/D', width: 96, height: 96 },
}

/** Up to five fit the sidebar header. `icon` picks the glyph in components/brand/SocialIcon.tsx. */
export const socials: SocialLink[] = [
  { label: 'LinkedIn', href: 'https://www.linkedin.com/in/carloalbertocueto26/', external: true, icon: 'linkedin' },
  { label: 'Facebook', href: 'https://www.facebook.com/carloalberto1226', external: true, icon: 'facebook' },
  { label: 'OnlineJobs.ph', href: 'https://www.onlinejobs.ph/jobseekers/info/3720312', external: true, icon: 'onlinejobs' },
]

export function mailtoHref(): string {
  return `mailto:${site.email}?subject=${encodeURIComponent(site.emailSubject)}`
}

/**
 * Carlo's mascot: chibi "robot kid" sprites (public/images/mascot), small decorative accents only.
 * width / height are the files' native pixels; components show them at a smaller CSS height and never
 * upscale. Decorative everywhere: alt="".
 */
export type MascotPose = 'point' | 'thumbsup' | 'think' | 'celebrate' | 'sit' | 'wave'

export const mascot: Record<MascotPose, { src: string; width: number; height: number }> = {
  point: { src: '/images/mascot/point.webp', width: 163, height: 266 },
  thumbsup: { src: '/images/mascot/thumbsup.webp', width: 180, height: 266 },
  think: { src: '/images/mascot/think.webp', width: 143, height: 243 },
  celebrate: { src: '/images/mascot/celebrate.webp', width: 221, height: 273 },
  sit: { src: '/images/mascot/sit.webp', width: 166, height: 226 },
  wave: { src: '/images/mascot/wave.webp', width: 183, height: 265 },
}
